import { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Search, Users, Phone, Mail, MapPin, Building,
  FileText, Receipt, HardHat, X, MessageSquare, Euro, Link2, Copy, ExternalLink
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { GPSLocationPicker } from '../../../components/ui/gps-location-picker';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

export default function ClientsPage() {
  const { user } = useOutletContext();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [newClient, setNewClient] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    postal_code: '',
    company_name: '',
    siret: ''
  });

  // Données DÉMO pour le mode sans authentification (VIDE pour nouvel utilisateur)
  const DEMO_CLIENTS = [];

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    const token = localStorage.getItem('client_token');
    
    // MODE DÉMO : Si pas de token, utiliser les données fictives
    if (!token) {
      setClients(DEMO_CLIENTS);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/batiment/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      // En cas d'erreur, utiliser les données démo
      setClients(DEMO_CLIENTS);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('client_token');

    try {
      const response = await fetch(`${API_URL}/api/batiment/clients`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newClient)
      });

      if (response.ok) {
        toast.success('Client créé avec succès');
        setShowCreateModal(false);
        resetForm();
        fetchClients();
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const openDetailModal = async (client) => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/batiment/clients/${client.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setSelectedClient(await response.json());
        setShowDetailModal(true);
      }
    } catch (error) {
      setSelectedClient(client);
      setShowDetailModal(true);
    }
  };

  const resetForm = () => {
    setNewClient({
      name: '',
      phone: '',
      email: '',
      address: '',
      city: '',
      postal_code: '',
      company_name: '',
      siret: ''
    });
  };

  const generatePortalLink = async (clientId) => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/batiment/clients/${clientId}/generate-portal-token`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const portalUrl = `${window.location.origin}/mon-chantier/${data.token}`;
        
        // Copy to clipboard
        await navigator.clipboard.writeText(portalUrl);
        toast.success('Lien copié dans le presse-papiers !');
        
        // Update selected client
        if (selectedClient?.id === clientId) {
          setSelectedClient(prev => ({ ...prev, portal_token: data.token }));
        }
        
        return data.token;
      } else {
        toast.error('Erreur lors de la génération du lien');
      }
    } catch (error) {
      toast.error('Erreur lors de la génération du lien');
    }
  };

  const copyPortalLink = async (portalToken) => {
    const portalUrl = `${window.location.origin}/mon-chantier/${portalToken}`;
    await navigator.clipboard.writeText(portalUrl);
    toast.success('Lien copié !');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredClients = clients.filter(c =>
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.company_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-rm-text">Clients</h1>
          <p className="text-rm-text-secondary">Ta base de données clients</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-rm-primary hover:bg-rm-primary/90">
          <Plus className="w-5 h-5 mr-2" />
          Nouveau client
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Rechercher un client..."
          className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
        />
      </div>

      {/* Clients Grid */}
      {filteredClients.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-12 text-center">
          <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-[#1E3A5F]/10 to-[#F97316]/10 rounded-full flex items-center justify-center">
            <Users className="w-10 h-10 text-[#1E3A5F]" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">Aucun client pour l'instant</h3>
          <p className="text-sm text-gray-500 mb-4">Ajoute ton premier client pour commencer à gérer tes devis et factures.</p>
          <Button onClick={() => setShowCreateModal(true)} className="bg-[#1E3A5F] hover:bg-[#2d4a6f]">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un client
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.map((client) => (
            <motion.div
              key={client.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => openDetailModal(client)}
              className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-[#1E3A5F]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-[#1E3A5F]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900 truncate">{client.name}</h3>
                  {client.company_name && (
                    <p className="text-xs text-gray-500 truncate">{client.company_name}</p>
                  )}
                </div>
              </div>

              <div className="mt-3 space-y-1.5 text-xs">
                {client.phone && (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Phone className="w-3.5 h-3.5" />
                    <span>{client.phone}</span>
                  </div>
                )}
                {client.city && (
                  <div className="flex items-center gap-2 text-gray-500">
                    <MapPin className="w-3.5 h-3.5" />
                    <span>{client.city}</span>
                  </div>
                )}
              </div>

              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                <div className="flex items-center gap-3">
                  <span className="text-gray-500">{client.total_quotes || 0} devis</span>
                  <span className="text-gray-500">{client.total_invoices || 0} factures</span>
                </div>
                <span className="font-semibold text-green-600">{formatCurrency(client.total_revenue)}</span>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-rm-text">Nouveau client</h2>
                  <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleCreateClient} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Nom *</label>
                  <input
                    type="text"
                    required
                    value={newClient.name}
                    onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Téléphone</label>
                    <input
                      type="tel"
                      value={newClient.phone}
                      onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Email</label>
                    <input
                      type="email"
                      value={newClient.email}
                      onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Entreprise</label>
                  <input
                    type="text"
                    value={newClient.company_name}
                    onChange={(e) => setNewClient({ ...newClient, company_name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Adresse</label>
                  <input
                    type="text"
                    value={newClient.address}
                    onChange={(e) => setNewClient({ ...newClient, address: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Ville</label>
                    <input
                      type="text"
                      value={newClient.city}
                      onChange={(e) => setNewClient({ ...newClient, city: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Code postal</label>
                    <input
                      type="text"
                      value={newClient.postal_code}
                      onChange={(e) => setNewClient({ ...newClient, postal_code: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>Annuler</Button>
                  <Button type="submit" className="bg-rm-primary">Créer le client</Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedClient && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-3xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-rm-text">{selectedClient.name}</h2>
                    {selectedClient.company_name && (
                      <p className="text-rm-text-secondary">{selectedClient.company_name}</p>
                    )}
                  </div>
                  <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Quick Actions */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {selectedClient.phone && (
                    <a href={`tel:${selectedClient.phone}`} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                      <Phone className="w-5 h-5 text-rm-primary" />
                      <span className="text-sm">Appeler</span>
                    </a>
                  )}
                  {selectedClient.phone && (
                    <a href={`https://wa.me/${selectedClient.phone.replace(/\s/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors text-green-700">
                      <MessageSquare className="w-5 h-5" />
                      <span className="text-sm">WhatsApp</span>
                    </a>
                  )}
                  <Link to={`/espace-client/crm-batiment/devis?action=new&client=${selectedClient.id}`} className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors text-amber-700">
                    <FileText className="w-5 h-5" />
                    <span className="text-sm">Devis</span>
                  </Link>
                  {selectedClient.email && (
                    <a href={`mailto:${selectedClient.email}`} className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors text-blue-700">
                      <Mail className="w-5 h-5" />
                      <span className="text-sm">Email</span>
                    </a>
                  )}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-rm-text">{selectedClient.total_quotes || 0}</p>
                    <p className="text-sm text-rm-text-secondary">Devis</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-rm-text">{selectedClient.total_invoices || 0}</p>
                    <p className="text-sm text-rm-text-secondary">Factures</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(selectedClient.total_revenue)}</p>
                    <p className="text-sm text-green-700">CA total</p>
                  </div>
                </div>

                {/* Portal Link Section */}
                <div className="bg-gradient-to-r from-rm-primary/5 to-rm-secondary/5 rounded-xl p-4 border border-rm-primary/10">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-rm-text flex items-center gap-2">
                        <Link2 className="w-4 h-4 text-rm-primary" />
                        Portail Client
                      </h4>
                      <p className="text-sm text-rm-text-secondary mt-1">
                        {selectedClient.portal_token 
                          ? 'Lien actif - Le client peut voir ses devis, factures et chantiers'
                          : 'Générez un lien pour donner accès au client à son espace'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {selectedClient.portal_token ? (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyPortalLink(selectedClient.portal_token)}
                            className="flex items-center gap-1"
                          >
                            <Copy className="w-4 h-4" />
                            Copier
                          </Button>
                          <a
                            href={`/mon-chantier/${selectedClient.portal_token}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-rm-primary text-white rounded-lg hover:bg-rm-primary/90"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Voir
                          </a>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => generatePortalLink(selectedClient.id)}
                            className="text-orange-600 border-orange-200 hover:bg-orange-50"
                          >
                            Régénérer
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => generatePortalLink(selectedClient.id)}
                          className="bg-rm-primary"
                        >
                          <Link2 className="w-4 h-4 mr-1" />
                          Générer le lien
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div>
                  <h4 className="font-semibold text-rm-text mb-3">Informations</h4>
                  <div className="space-y-2 text-sm">
                    {selectedClient.phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span>{selectedClient.phone}</span>
                      </div>
                    )}
                    {selectedClient.email && (
                      <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span>{selectedClient.email}</span>
                      </div>
                    )}
                    {selectedClient.address && (
                      <div className="flex items-center gap-3">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        <span>{selectedClient.address}, {selectedClient.postal_code} {selectedClient.city}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* GPS Location for Pointage */}
                <GPSLocationPicker
                  targetType="client"
                  targetId={selectedClient.id}
                  currentLatitude={selectedClient.latitude}
                  currentLongitude={selectedClient.longitude}
                  onUpdate={(coords) => {
                    setSelectedClient(prev => ({ ...prev, ...coords }));
                    setClients(prev => prev.map(c => 
                      c.id === selectedClient.id ? { ...c, ...coords } : c
                    ));
                  }}
                />

                {/* Quotes */}
                {selectedClient.quotes?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-rm-text mb-3">Devis</h4>
                    <div className="space-y-2">
                      {selectedClient.quotes.map(quote => (
                        <Link
                          key={quote.id}
                          to={`/espace-client/crm-batiment/devis/${quote.id}`}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100"
                        >
                          <div>
                            <p className="font-medium">{quote.quote_number}</p>
                            <p className="text-xs text-rm-text-secondary">{formatDate(quote.created_at)}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{formatCurrency(quote.total)}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              quote.status === 'accepted' ? 'bg-green-100 text-green-600' :
                              quote.status === 'sent' ? 'bg-blue-100 text-blue-600' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {quote.status === 'draft' ? 'Brouillon' :
                               quote.status === 'sent' ? 'Envoyé' :
                               quote.status === 'accepted' ? 'Accepté' : quote.status}
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Chantiers */}
                {selectedClient.chantiers?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-rm-text mb-3">Chantiers</h4>
                    <div className="space-y-2">
                      {selectedClient.chantiers.map(chantier => (
                        <Link
                          key={chantier.id}
                          to={`/espace-client/crm-batiment/chantiers/${chantier.id}`}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100"
                        >
                          <div>
                            <p className="font-medium">{chantier.address}</p>
                            <p className="text-xs text-rm-text-secondary capitalize">{chantier.chantier_type?.replace('_', ' ')}</p>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            chantier.status === 'completed' ? 'bg-green-100 text-green-600' :
                            chantier.status === 'in_progress' ? 'bg-purple-100 text-purple-600' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {chantier.status === 'to_plan' ? 'À planifier' :
                             chantier.status === 'in_progress' ? 'En cours' :
                             chantier.status === 'completed' ? 'Terminé' : chantier.status}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-slate-100 flex justify-end">
                <Button onClick={() => setShowDetailModal(false)}>Fermer</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
